import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { sortBy } from "lodash";
import {
  DetailFieldsContext,
  DetailFieldsSection,
  FieldsConfig
} from "@/components/layouts/DetailFields";
import { EntityData } from "@/services/api/types";
import { useEntityFields, useFieldsSections } from "@/pages/settings/hooks";
import { APIFieldItem, FieldSection } from "@/pages/settings/types";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useThresholds } from "@/entities/settings/hooks";
import { createFieldsConfigFromAPI } from "@/utils/entityFieldsUtils/detailFields";
import {
  FieldSectionPage,
  getPageFieldSections
} from "@/utils/entityFieldsUtils/fieldSections";
import { useNewFieldsUI } from "@/services/features/useNewFieldsUI";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";

export const useEntityFieldsConfig = (
  entityType: EntityType,
  entityData?: any,
  context?: any
): FieldsConfig => {
  const { t } = useTranslation();
  const { thresholds } = useThresholds();
  const { data: entityFieldsResponse } = useEntityFields(entityType);
  const isNewFieldsUI = useNewFieldsUI();

  return useMemo(
    () =>
      createFieldsConfigFromAPI(
        entityFieldsResponse?.fields,
        entityData,
        entityType,
        t,
        thresholds,
        context,
        isNewFieldsUI
      ),
    [
      entityFieldsResponse?.fields,
      entityData,
      entityType,
      t,
      thresholds,
      context,
      isNewFieldsUI
    ]
  );
};

export interface FieldsBySection {
  unsectionedFields: APIFieldItem[];
  sections: { section: FieldSection; fields: APIFieldItem[] }[];
}

// A field whose section no longer exists stays with the unsectioned fields, so
// a deleted section never hides the fields that pointed at it.
export const groupFieldsBySection = (
  fields: APIFieldItem[],
  fieldsSections: FieldSection[]
): FieldsBySection => {
  const sectionIds = new Set(
    fieldsSections.map((section) => String(section.id))
  );
  const unsectionedFields: APIFieldItem[] = [];
  const fieldsBySectionId: Record<string, APIFieldItem[]> = {};

  fields.forEach((field) => {
    const sectionId = field.section == null ? null : String(field.section);
    if (sectionId === null || !sectionIds.has(sectionId)) {
      unsectionedFields.push(field);
      return;
    }
    (fieldsBySectionId[sectionId] ||= []).push(field);
  });

  const sections = sortBy(fieldsSections, "order")
    .filter((section) => fieldsBySectionId[String(section.id)]?.length)
    .map((section) => ({
      section,
      fields: sortBy(fieldsBySectionId[String(section.id)], "order")
    }));

  return { unsectionedFields, sections };
};

// Keeps the built config's field order; fields the builder dropped (hidden
// from the detailed view) are skipped
const pickFieldsConfig = (
  fieldsConfig: FieldsConfig,
  fields: APIFieldItem[]
): FieldsConfig => {
  const fieldKeys = new Set(fields.map((field) => field.fieldKey));
  return Object.fromEntries(
    Object.entries(fieldsConfig).filter(([fieldKey]) => fieldKeys.has(fieldKey))
  );
};

const dropEmptySections = (
  sections: DetailFieldsSection[]
): DetailFieldsSection[] =>
  sections.filter((section) => Object.keys(section.fieldsConfig).length > 0);

/**
 * Applies a consumer's config tweaks to every section too, so a field keeps
 * its behaviour when an admin moves it into a section. Sections the tweak
 * leaves empty are dropped.
 */
export const mapSectionsFieldsConfig = (
  sections: DetailFieldsSection[],
  transform: (fieldsConfig: FieldsConfig) => FieldsConfig
): DetailFieldsSection[] =>
  dropEmptySections(
    sections.map((section) => ({
      ...section,
      fieldsConfig: transform(section.fieldsConfig)
    }))
  );

export interface EntityDetailFieldsLayout {
  fieldsConfig: FieldsConfig;
  sections: DetailFieldsSection[];
}

/**
 * Detailed-view fields split into the unsectioned ones and one group per
 * section, in section order. Pass `groupBySection: false` where the layout has
 * no room for section headers (the quick view), which returns every field in
 * `fieldsConfig`, same as `useEntityFieldsConfig`.
 */
export const useEntityDetailFieldsLayout = (
  entityType: EntityType,
  entityData?: EntityData,
  context?: DetailFieldsContext,
  { groupBySection = true }: { groupBySection?: boolean } = {}
): EntityDetailFieldsLayout => {
  const { t } = useTranslation();
  const { thresholds } = useThresholds();
  const { enabled: isDetailedViewSectionsEnabled } = useFeatureFlag(
    FeatureFlags.CRM_10267_DETAILED_VIEW_SECTIONS
  );
  const shouldGroupBySection = groupBySection && isDetailedViewSectionsEnabled;
  const { data: entityFieldsResponse } = useEntityFields(entityType);
  const { data: fieldsSectionsResponse } = useFieldsSections(entityType, {
    enabled: shouldGroupBySection
  });
  const isNewFieldsUI = useNewFieldsUI();

  return useMemo(() => {
    const fields = entityFieldsResponse?.fields;
    // Built once from every field, then split: some fields are wired to
    // others (partner contact to partner), which only resolves when the
    // builder sees the whole list
    const fullFieldsConfig = createFieldsConfigFromAPI(
      fields,
      entityData,
      entityType,
      t,
      thresholds,
      context,
      isNewFieldsUI
    );

    // Fields in Additional Info tab sections stay flat here, as before sections
    const fieldsSections = getPageFieldSections(
      fieldsSectionsResponse?.fieldsSections ?? [],
      FieldSectionPage.DETAILED_VIEW
    );
    if (!shouldGroupBySection || !fields || !fieldsSections.length) {
      return { fieldsConfig: fullFieldsConfig, sections: [] };
    }

    const { unsectionedFields, sections } = groupFieldsBySection(
      fields,
      fieldsSections
    );
    return {
      fieldsConfig: pickFieldsConfig(fullFieldsConfig, unsectionedFields),
      sections: dropEmptySections(
        sections.map(({ section, fields: sectionFields }) => ({
          id: section.id,
          name: section.name,
          fieldsConfig: pickFieldsConfig(fullFieldsConfig, sectionFields)
        }))
      )
    };
  }, [
    shouldGroupBySection,
    entityFieldsResponse?.fields,
    fieldsSectionsResponse?.fieldsSections,
    entityData,
    entityType,
    t,
    thresholds,
    context,
    isNewFieldsUI
  ]);
};
